import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Button,
  TextInput,
  Alert,
  Group,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconAlertTriangle,
  IconTrash,
} from '@tabler/icons-react';
import { useAuth } from '../../context/AuthContext';

export default function DeleteAccount() {
  const navigate = useNavigate();
  const { user, deleteAccount } = useAuth();
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');

  const expectedConfirmation = 'DELETE';

  const deleteAccountMutation = useMutation({
    mutationFn: () => deleteAccount(),
    onSuccess: () => navigate('/', { replace: true }),
    onError: () => setError('Failed to delete account. Please try again.'),
  });

  const handleDelete = () => {
    if (confirmation !== expectedConfirmation) {
      setError(`Please type "${expectedConfirmation}" to confirm`);
      return;
    }
    setError('');
    deleteAccountMutation.mutate();
  };

  if (!user) {
    return null;
  }

  return (
    <Container size="sm" pt="md" pb="xl">
      <Button
        variant="subtle"
        size="compact-sm"
        leftSection={<IconArrowLeft size={14} />}
        onClick={() => navigate('/settings')}
        mb="xs"
      >
        Back to Settings
      </Button>

      <Title order={2} mb="lg">
        Delete Account
      </Title>

      <Alert
        icon={<IconAlertTriangle size={16} />}
        color="red"
        title="This action is permanent"
        mb="lg"
      >
        <Text size="sm">
          Deleting your account will permanently remove all your data, including:
        </Text>
        <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
          <li><Text size="sm">All your reviews and ratings</Text></li>
          <li><Text size="sm">All your comments</Text></li>
          <li><Text size="sm">Your followers and following lists</Text></li>
          <li><Text size="sm">Your profile and account information</Text></li>
        </ul>
        <Text size="sm" fw={600}>
          This cannot be undone.
        </Text>
      </Alert>

      <Card shadow="sm" padding="lg" radius="md" withBorder style={{ borderColor: 'var(--mantine-color-red-3)' }}>
        <Stack gap="md">
          <Group gap="xs">
            <IconTrash size={20} color="var(--mantine-color-red-6)" />
            <Title order={4}>Confirm Deletion</Title>
          </Group>

          <Text size="sm">
            To confirm, type <Text span fw={700}>DELETE</Text> in the field below:
          </Text>

          {error && (
            <Alert color="red" icon={<IconAlertTriangle size={16} />}>
              {error}
            </Alert>
          )}

          <TextInput
            placeholder='Type "DELETE" to confirm'
            value={confirmation}
            onChange={(e) => setConfirmation(e.currentTarget.value)}
          />

          <Button
            color="red"
            fullWidth
            onClick={handleDelete}
            loading={deleteAccountMutation.isPending}
            disabled={confirmation !== expectedConfirmation}
          >
            Permanently Delete My Account
          </Button>
        </Stack>
      </Card>
    </Container>
  );
}
